import React from "react";
import { Link } from "react-router-dom";
import { Share2, MapPin, Globe, Users, FileSearch, GitBranch, ArrowRight } from "lucide-react";
import { Btn } from "@/components/common";

const HERO = "https://images.unsplash.com/photo-1729161769741-e7bbb82abc84?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMG5ldHdvcmslMjBkYXJrJTIwYmx1ZXxlbnwwfHx8fDE3OTA4NTI1Mjl8MA&ixlib=rb-4.1.0&q=85";
const CONCEPT = "https://images.unsplash.com/photo-1621947081720-86970823b77a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMG5ldHdvcmslMjBkYXJrJTIwYmx1ZXxlbnwwfHx8fDE3OTA4NTI1Mjl8MA&ixlib=rb-4.1.0&q=85";
const DESK = "https://static.prod-images.emergentagent.com/jobs/ab67736f-b929-49f7-9fb0-60b16f378989/images/f856de74447079b43240966190c5bdc553ff4de0ad91d81f40761a8a7246fe55.jpeg";

const ORIGINS = [
  { icon: MapPin, t: "A place", d: "An office, building, neighborhood, or geographical area." },
  { icon: Globe, t: "A digital space", d: "A website, directory, online community, or professional group." },
  { icon: Users, t: "An actor", d: "A person, business, organization, or event." },
];

// Understated spider-web illustration: concentric web strands + radial spokes with connected points.
function SpiderWeb() {
  const cx = 200, cy = 150;
  const angles = [0, 45, 90, 135, 180, 225, 270, 315];
  const rings = [125, 78, 34];
  const pt = (r, a) => {
    const rad = (a * Math.PI) / 180;
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
  };
  const polyOf = (r) => angles.map((a) => pt(r, a).map((n) => n.toFixed(1)).join(",")).join(" ");
  return (
    <svg viewBox="0 0 400 300" width="100%" style={{ maxWidth: 440, height: "auto", display: "block" }}
         role="img" aria-label="Spider Contact network of connected points" data-testid="spider-web-illustration">
      <g fill="none" stroke="var(--si-silver)" strokeWidth="1" opacity="0.28">
        {rings.map((r) => <polygon key={r} points={polyOf(r)} />)}
        {angles.map((a) => {
          const [x, y] = pt(125, a);
          return <line key={a} x1={cx} y1={cy} x2={x.toFixed(1)} y2={y.toFixed(1)} />;
        })}
      </g>
      <g fill="var(--si-teal-light)" opacity="0.85">
        {angles.map((a) => {
          const [x, y] = pt(125, a);
          return <circle key={a} cx={x.toFixed(1)} cy={y.toFixed(1)} r="3.5" />;
        })}
        {angles.filter((_, i) => i % 2 === 0).map((a) => {
          const [x, y] = pt(78, a);
          return <circle key={`m${a}`} cx={x.toFixed(1)} cy={y.toFixed(1)} r="2.5" opacity="0.7" />;
        })}
      </g>
      <circle cx={cx} cy={cy} r="5" fill="var(--si-silver)" />
    </svg>
  );
}

export default function Marketing() {
  return (
    <div style={{ background: "var(--si-bg)", minHeight: "100vh" }}>
      {/* nav */}
      <header className="sticky top-0 z-50" style={{ background: "rgba(5,11,20,0.6)", backdropFilter: "blur(16px)", borderBottom: "1px solid rgba(139,164,181,0.2)" }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="font-display text-lg" style={{ color: "var(--si-text)" }}>Strategic Interest</div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login"><Btn variant="ghost" data-testid="nav-login-btn">Sign in</Btn></Link>
            <Link to="/register"><Btn variant="primary" data-testid="nav-register-btn">Get started <ArrowRight size={15} /></Btn></Link>
          </div>
        </div>
      </header>

      {/* hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: `url(${HERO})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0" style={{ background: "rgba(5,11,20,0.72)" }} />
        <div className="absolute inset-0 si-grain" />
        <div className="relative max-w-6xl mx-auto px-6 pt-28 pb-32">
          <div className="si-label mb-6 flex items-center gap-2"><Share2 size={14} strokeWidth={1.5} /> Relationship Intelligence</div>
          <h1 className="font-display font-light max-w-3xl" style={{ color: "var(--si-text)", fontSize: "clamp(2.5rem,6vw,4.5rem)", lineHeight: 1.05 }}>
            Map the network around any<span style={{ color: "var(--si-silver)" }}> point of origin</span>.
          </h1>
          <p className="mt-8 max-w-xl text-lg leading-relaxed" style={{ color: "var(--si-text-2)" }}>
            Strategic Interest brings together entity files, documented relationships, research, and
            follow-up actions — built around <span style={{ color: "var(--si-silver)" }}>Spider Contact</span>, a
            method for discovering and preserving the people, places, and organizations connected to a starting point.
          </p>
          <div className="mt-10 flex gap-4 flex-wrap">
            <Link to="/register"><Btn variant="primary" data-testid="hero-start-btn">Start building a network <ArrowRight size={16} /></Btn></Link>
            <Link to="/login"><Btn variant="secondary">I already have an account</Btn></Link>
          </div>
        </div>
      </section>

      {/* origin types */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="si-label mb-3">The method</div>
        <h2 className="text-3xl sm:text-4xl font-medium tracking-tight max-w-2xl" style={{ color: "var(--si-text)" }}>
          Every Spider Contact network begins with a point of origin.
        </h2>
        <p className="mt-4 max-w-2xl" style={{ color: "var(--si-text-2)" }}>
          The entities surrounding that point form its initial network — defined through physical proximity,
          digital membership, or documented relationships. Any entity can become a new origin.
        </p>
        <div className="grid md:grid-cols-3 mt-12" style={{ border: "1px solid var(--si-border)" }}>
          {ORIGINS.map((o, i) => (
            <div key={o.t} className="p-8" style={{ borderRight: i < 2 ? "1px solid var(--si-border)" : "none", background: "var(--si-surface)" }}>
              <o.icon size={22} strokeWidth={1.5} style={{ color: "var(--si-silver)" }} />
              <div className="mt-4 text-lg" style={{ color: "var(--si-text)" }}>{o.t}</div>
              <div className="mt-2 text-sm" style={{ color: "var(--si-text-2)" }}>{o.d}</div>
            </div>
          ))}
        </div>
        <div className="mt-8 p-6 font-mono text-sm" style={{ background: "var(--si-surface)", border: "1px solid var(--si-border)", color: "var(--si-silver)", borderRadius: 2 }}>
          My office → neighboring business → business owner → local association → association members → additional networks
        </div>
      </section>

      {/* contact web */}
      <section style={{ borderTop: "1px solid var(--si-border)", borderBottom: "1px solid var(--si-border)" }}>
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="si-label mb-3 flex items-center gap-2"><GitBranch size={14} strokeWidth={1.5} /> The Contact Web</div>
          <h2 className="text-3xl sm:text-4xl font-medium tracking-tight max-w-3xl" style={{ color: "var(--si-text)" }}>
            Every contact connects to other contacts.
          </h2>
          <p className="mt-4 max-w-2xl" style={{ color: "var(--si-text-2)" }}>
            Inside Spider Contact, each entity file carries an interactive Contact Web — a living graph of who
            knows whom. Connect any contact to another, document how you know it, and explore one, two, or three
            degrees of connection. Local and digital relationships live in the same web, each contact keeps a
            single canonical record, and documented paths show how two people are linked — without ever implying
            permission to introduce or contact anyone.
          </p>
          <div className="grid md:grid-cols-3 mt-10" style={{ border: "1px solid var(--si-border)" }}>
            {[
              { t: "Connect & verify", d: "Knows, introduced by, works for, member of, neighbor of, and more — each with direction, source, and a confirmed / reported / inferred / disputed state." },
              { t: "Explore degrees", d: "Recenter on any contact, expand or collapse branches, and bound large webs so they stay readable." },
              { t: "Paths & shared ties", d: "Highlight the documented path between two contacts and reveal the connections they share." },
            ].map((x, i) => (
              <div key={x.t} className="p-8" style={{ borderRight: i < 2 ? "1px solid var(--si-border)" : "none", background: "var(--si-surface)" }}>
                <div className="font-mono text-xs" style={{ color: "var(--si-teal-light)" }}>{String(i + 1).padStart(2, "0")}</div>
                <div className="mt-3 text-lg" style={{ color: "var(--si-text)" }}>{x.t}</div>
                <div className="mt-2 text-sm" style={{ color: "var(--si-text-2)" }}>{x.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* concept */}
      <section className="relative overflow-hidden" style={{ borderTop: "1px solid var(--si-border)", borderBottom: "1px solid var(--si-border)" }}>
        <div className="max-w-6xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="si-label mb-3">What you preserve</div>
            <h2 className="text-3xl font-medium tracking-tight" style={{ color: "var(--si-text)" }}>
              Observations kept, even when usefulness is unknown.
            </h2>
            <p className="mt-4" style={{ color: "var(--si-text-2)" }}>
              Every discovered entity can receive a record. Information moves through distinct states —
              from a discovered candidate to a verified identity, a documented connection, an established
              relationship, and permission to communicate.
            </p>
            <ul className="mt-6 space-y-3">
              {["Discovered candidate", "Verified identity", "Documented connection", "Established relationship", "Permission to communicate"].map((s, i) => (
                <li key={s} className="flex items-center gap-3 text-sm" style={{ color: "var(--si-text)" }}>
                  <span className="font-mono text-xs" style={{ color: "var(--si-teal-light)" }}>{String(i + 1).padStart(2, "0")}</span>{s}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative" style={{ aspectRatio: "4/3" }}>
            <img src={CONCEPT} alt="Network concept" className="w-full h-full object-cover" style={{ borderRadius: 2 }} />
            <div className="absolute inset-0" style={{ background: "rgba(5,11,20,0.3)", borderRadius: 2 }} />
          </div>
        </div>
      </section>

      {/* features grid */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-3" style={{ border: "1px solid var(--si-border)" }}>
          {[
            { icon: Share2, t: "Coordinated views", d: "Relationship graph, geographic map, and searchable table — always in agreement." },
            { icon: FileSearch, t: "Research & evidence", d: "Attach sources, record observations, and keep conflicting information visibly uncertain." },
            { icon: GitBranch, t: "Review & dedup", d: "Resolve candidates, incomplete identities, and possible duplicates with a full audit trail." },
          ].map((f, i) => (
            <div key={f.t} className="p-8" style={{ borderRight: i < 2 ? "1px solid var(--si-border)" : "none", background: "var(--si-surface)" }}>
              <f.icon size={20} strokeWidth={1.5} style={{ color: "var(--si-teal-light)" }} />
              <div className="mt-4 text-lg" style={{ color: "var(--si-text)" }}>{f.t}</div>
              <div className="mt-2 text-sm" style={{ color: "var(--si-text-2)" }}>{f.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* capabilities available today */}
      <section style={{ borderTop: "1px solid var(--si-border)", background: "var(--si-surface)" }}>
        <div className="max-w-6xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="si-label mb-3">Available today</div>
            <ul className="space-y-5">
              {[
                "Build a fuller understanding of every contact with profiles, personality observations, and notes collected over time.",
                "Capture contacts by voice, explore their connections, and prepare for conversations with source-linked summaries.",
                "Keep relationships useful as circumstances change, with importance classifications, follow-ups, backups, and printable reports.",
              ].map((line, i) => (
                <li key={i} className="flex items-start gap-3 text-base leading-relaxed" style={{ color: "var(--si-text)" }} data-testid={`capability-line-${i}`}>
                  <span className="font-mono text-xs mt-1.5 shrink-0" style={{ color: "var(--si-teal-light)" }}>{String(i + 1).padStart(2, "0")}</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="order-first md:order-last flex justify-center">
            <SpiderWeb />
          </div>
        </div>
      </section>

      {/* attribution */}
      <section style={{ borderTop: "1px solid var(--si-border)" }}>
        <div className="max-w-6xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-16 items-center">
          <div className="relative order-2 md:order-1" style={{ aspectRatio: "4/3" }}>
            <img src={DESK} alt="Worldwide network of connections" className="w-full h-full object-cover" style={{ borderRadius: 2 }} />
          </div>
          <div className="order-1 md:order-2">
            <div className="si-label mb-3">Origin of the concept</div>
            <h2 className="font-display text-3xl sm:text-4xl" style={{ color: "var(--si-text)" }}>
              The Spider Contact method
            </h2>
            <p className="mt-5" style={{ color: "var(--si-text-2)" }}>
              Spider Contact is a disciplined approach to relationship discovery: start from a known point,
              document what surrounds it, and expand outward one deliberate step at a time — never fabricating
              connections, always preserving what is uncertain.
            </p>
          </div>
        </div>
      </section>

      {/* cta */}
      <section className="max-w-6xl mx-auto px-6 py-24 text-center">
        <h2 className="text-3xl sm:text-4xl font-medium tracking-tight" style={{ color: "var(--si-text)" }}>
          Build your first network today.
        </h2>
        <div className="mt-8 flex justify-center">
          <Link to="/register"><Btn variant="primary" data-testid="cta-register-btn">Create your workspace <ArrowRight size={16} /></Btn></Link>
        </div>
      </section>
    </div>
  );
}
